import React, { useEffect } from "react";
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonInput,
  IonItem,
} from "@ionic/react";
import { useParams } from "react-router";
import { useState } from "react";
import "./SetPasswordPage.css";

// images
import iconHidePassword from "../assets/Button_Display_Hide@3x.png";
import iconShowPassword from "../assets/Button_Display_Show@3x.png";
import classAppHeaderImage from "../assets/reset-password-header.png";
import classAppHeaderImageCanada from "../assets/reset-password-header.canada02.png";

const isCanada = window.location.origin.includes(".ca") || window.location.origin.includes("canada");

const SetPasswordPage: React.FC = () => {
  const [token, setToken] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordValidationError, setPasswordValidationError] = useState<string>("");
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [confirmPasswordValidationError, setConfirmPasswordValidationError] = useState<string>("");
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [isErrorMessage, setIsErrorMessage] = useState<boolean>(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");
    const email = queryParams.get("email");
    if (token != null) {
      setToken(token);
    }
    if (email != null) {
      setEmail(email);
    }
  }, []);

  async function sendPasswordReset() {
    try {
      let url = "https://class.aimernginx.co.uk/pwb/setPassword";
      if (isCanada) {
        url = "https://classcanadainstance1.class-services.ca/classapi/SF/resetPassword";
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email, newPassword: password, token: token }),
      });

      if (response) {
        const data = await response.json();
        if (data.success || response.status === 200) {
          setIsErrorMessage(false);
          setMessage(data.message);
        } else {
          let message = data.error;
          if (message === "jwt expired") {
            message = "The password reset link has expired. Please request a new one.";
          }
          setIsErrorMessage(true);
          setMessage(message);
        }
      } else {
        setIsErrorMessage(true);
        setMessage("Failed to reset password. Please try again");
      }
    } catch (error) {
      setIsErrorMessage(true);
      setMessage("An error occurred while trying to reset password. Please try again");
    }
  }

  // validate password fields when they change
  useEffect(() => {
    const passwordValid = password.length > 0 && validatePassword(password);
    if (passwordValid) {
      setPasswordValidationError("");
    } else {
      setPasswordValidationError(
        "Password must be 14 characters or longer, contain at least one uppercase letter, one lowercase letter, and one number"
      );
    }

    const confirmPasswordValid = confirmPassword.length > 0 && password === confirmPassword;
    if (confirmPasswordValid) {
      setConfirmPasswordValidationError("");
    } else {
      setConfirmPasswordValidationError("Passwords must match");
    }
  }, [password, confirmPassword]);

  function validatePassword(password: string) {
    // password must be 14 characters or longer, contain at least one uppercase letter, one lowercase letter, and one number
    const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{14,}$/;
    if (!passwordRegex.test(password)) {
      return false;
    } else {
      return true;
    }
  }

  return (
    <IonPage>
      <IonHeader></IonHeader>

      <IonContent className="email-reset-ion-content" fullscreen>
        <img src={isCanada ? classAppHeaderImageCanada : classAppHeaderImage} />

        <div className="email-reset-main-content">
          <h1 className="email-reset-title">Set Your App Password</h1>
          <div className="content-text">
            To set your password, please complete the below.
          </div>
          <br />
          <div className="content-text">
            If you are using the same email address for multiple of Class Professionals apps, your password is the same for all of those
            apps. Your new password needs to be 14 characters or longer, contain at least one uppercase letter, one lowercase letter and one number.
          </div>

          {message ? (
            <div className={`email-reset-message ${isErrorMessage ? "error-message" : ""}`}>{message}</div>
          ) : null}
          
          <div className="email-input-container">
            <div className="input-label">Password</div>

            <IonItem className="ion-no-padding">
              <IonInput
                value={password}
                type={passwordVisible ? "text" : "password"}
                mode="ios"
                className="input-field"
                onIonInput={(event) => setPassword(event.detail.value!)}
              />
              <IonButton fill="clear" className="ion-no-padding" onClick={() => setPasswordVisible(!passwordVisible)}>
                <img alt="" src={passwordVisible ? iconHidePassword : iconShowPassword} className="pass-icon" />
              </IonButton>
            </IonItem>
            {password.length > 0 && passwordValidationError.length > 0 ? (
              <div className="validation-error-message">{passwordValidationError}</div>
            ) : null}

            <br />

            <div className="input-label">Confirm Password</div>

            <IonItem className="ion-no-padding">
              <IonInput
                value={confirmPassword}
                type={confirmPasswordVisible ? "text" : "password"}
                mode="ios"
                className="input-field"
                onIonInput={(event) => setConfirmPassword(event.detail.value!)}
              />
              <IonButton fill="clear" className="ion-no-padding" onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}>
                <img alt="" src={confirmPasswordVisible ? iconHidePassword : iconShowPassword} className="pass-icon" />
              </IonButton>
            </IonItem>
            {confirmPassword.length > 0 && confirmPasswordValidationError.length > 0 ? (
              <div className="validation-error-message">{confirmPasswordValidationError}</div>
            ) : null}

            <IonButton
              className="filled-button send-password-button"
              disabled={
                password.length === 0 ||
                confirmPassword.length === 0 ||
                passwordValidationError !== "" ||
                confirmPasswordValidationError !== ""
              }
              onClick={() => sendPasswordReset()}
            >
              Set Password
            </IonButton>
          </div>

          {isCanada ? (
            <div className="content-text">
              If you have any questions or feedback, please let us know: email <a href="mailto:support@classprofessional.ca">support@classprofessional.ca</a>
            </div>
          ) : (
            <div className="content-text">
              If you have any questions or feedback, please let us know: email <a href="mailto:apps@class.co.uk">apps@class.co.uk</a> or call{" "}
              <a href="tel:01278427800">01278 427800</a>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SetPasswordPage;
